"use client";

import React, { useState, useEffect } from "react";
import { useThunk } from "@/hooks/useThunk";
import { fetchPositions, submitApplication } from "@/utils/api/careerApi";
import { Upload } from "lucide-react";
import { useInput } from "@/hooks/use-input";
import { useFileInput } from "@/hooks/use-file-input";
import {
  validateFirstName,
  validateLastName,
  validatePosition,
  validateCoverLetter,
  validateFile,
} from "@/utils/validationUtils";
import Dropdown from "@/components/common/Dropdown";
import Input from "@/components/common/Input";
import { FormLabel } from "@/components/common/FormUtils";
import Button from "@/components/common/Button";
import showToast from "@/utils/toastNotifications";
import { twMerge } from "tailwind-merge";
import { useIsDark } from "@/context/ThemeContext";
// positions will be fetched from the server

export default function CareersForm() {
  const isDark = useIsDark();

  const {
    value: firstName,
    handleInputChange: handleFirstNameChange,
    handleInputBlur: handleFirstNameBlur,
    hasError: firstNameHasError,
    errorMessage: firstNameErrorMessage,
    reset: resetFirstName,
  } = useInput("", validateFirstName);

  const {
    value: lastName,
    handleInputChange: handleLastNameChange,
    handleInputBlur: handleLastNameBlur,
    hasError: lastNameHasError,
    errorMessage: lastNameErrorMessage,
    reset: resetLastName,
  } = useInput("", validateLastName);

  // Positions will be fetched from the server (active & accepting applications)
  const [positionOptions, setPositionOptions] = useState([]);
  const [runFetchPositions, isFetchingPositions] = useThunk(fetchPositions);
  const [runSubmitApplication, isSubmittingApp] = useThunk(submitApplication);

  const {
    value: positionOption,
    setValue: setPositionOption,
    handleInputBlur: handlePositionBlur,
    hasError: positionHasError,
    errorMessage: positionErrorMessage,
    reset: resetPosition,
  } = useInput("", validatePosition);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { success, response } = await runFetchPositions();
        const data = response?.data || [];
        const opts = data.map((p) => ({
          _id: String(p.id),
          name: `${p.position_name}`,
          rawName: p.position_name,
          code: p.position_code,
        }));
        if (mounted) {
          setPositionOptions(opts);
        }
      } catch (err) {
        console.error("Failed to fetch positions", err);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const {
    value: coverLetter,
    handleInputChange: handleCoverChange,
    handleInputBlur: handleCoverBlur,
    hasError: coverHasError,
    errorMessage: coverErrorMessage,
    setErrorMessage: setCoverExternalError,
    reset: resetCover,
  } = useInput("", validateCoverLetter);

  const handleCoverChangeLive = (e) => {
    const value = e.target.value;

    handleCoverChange(e);

    const error = validateCoverLetter(value);
    setCoverExternalError(error);
  };
  const coverCharCount = coverLetter.length;
  const maxCharCount = coverCharCount < 300 ? 300 : 600;
  const isCoverCharCountValid = coverCharCount >= 300 && coverCharCount <= 600;

  const countWords = (text) => {
    if (!text) return 0;
    return text.trim().split(/\s+/).filter(Boolean).length;
  };

  const coverWordCount = countWords(coverLetter);

  const [isDragging, setIsDragging] = useState(false);
  const {
    file,
    handleFileChange,
    handleFileDrop,
    handleFileBlur,
    handleFileDialogStart,
    hasError: fileHasError,
    error: fileError,
    reset: resetFile,
  } = useFileInput(validateFile);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFileDrop(f);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const resetForm = () => {
    resetPosition();
    resetCover();
    resetFile();
    resetFirstName();
    resetLastName();
  };

  const isFormValid =
    firstName &&
    lastName &&
    positionOption &&
    !positionErrorMessage &&
    !firstNameErrorMessage &&
    !lastNameErrorMessage &&
    coverLetter &&
    !coverErrorMessage &&
    file &&
    !fileHasError;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isFormValid) {
      showToast("error", "Please fill in all required fields correctly.");
      return;
    }

    setIsSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("firstName", firstName);
      fd.append("lastName", lastName);
      fd.append(
        "position",
        positionOption?.rawName || positionOption?.name || "",
      );
      fd.append("positionId", positionOption?._id || "");
      fd.append("coverLetter", coverLetter);
      fd.append("cv", file);

      const { success, response, error } = await runSubmitApplication(fd);

      if (success) {
        const successMessage =
          response?.message || "Application submitted successfully!";
        showToast("success", successMessage);
        resetForm();
      } else {
        const errMsg = response?.message || error?.message || "Upload failed";
        showToast("error", errMsg);
      }
    } catch (err) {
      console.error(err);
      showToast("error", "Submit failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      className="w-full max-w-[20000px] mx-auto mt-12 mb-20"
      onSubmit={handleSubmit}
    >
      <div className="dark:bg-[#0F1116] bg-[#FFFFFF] backdrop-blur-sm rounded-lg p-8 md:p-12 shadow-2xl">
        <h2
          className={twMerge(
            "font-inter text-2xl sm:text-3xl md:text-4xl 3xl:text-5xl 4xl:text-[52px] font-semibold mb-8 text-center w-full",
            "bg-clip-text text-transparent block mx-auto",
            isDark
              ? "bg-[linear-gradient(93.54deg,#FF1CF7_45.34%,#EEEEFF_65.74%)]"
              : "bg-[linear-gradient(93.54deg,#FF1CF7_45.34%,#1A1A1A_65.74%)]",
          )}
        >
          CAREERS AT ICIEOS
        </h2>

        <div className="space-y-6">
          {/* First + Last name row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-4 laptop:gap-5 3xl:gap-6 4xl:gap-7">
            <div>
              <Input
                id="firstName"
                label="First Name"
                isRequired
                placeholder="First name"
                value={firstName}
                onChange={handleFirstNameChange}
                onBlur={handleFirstNameBlur}
                errorMessage={firstNameErrorMessage}
                className="font-[Alexandria] text-sm lg:text-[16px] font-light dark:text-[#7F7F7F] text-[#5A5A6E] dark:bg-[#0F1116] bg-[#FFFFFF]"
              />
            </div>

            <div>
              <Input
                id="lastName"
                label="Last Name"
                isRequired
                placeholder="Last name"
                value={lastName}
                onChange={handleLastNameChange}
                onBlur={handleLastNameBlur}
                errorMessage={lastNameErrorMessage}
                className="font-[Alexandria] text-sm lg:text-[16px] font-light dark:text-[#7F7F7F] text-[#5A5A6E] dark:bg-[#0F1116] bg-[#FFFFFF]"
              />
            </div>
          </div>
          {/* Position Dropdown */}
          <FormLabel htmlFor="position" label="Position" isRequired />
          <Dropdown
            options={positionOptions}
            placeholder="Select a position"
            isRequired
            defaultOption={positionOption || null}
            onSelect={(opt) => setPositionOption(opt ? opt : null)}
            onBlur={handlePositionBlur}
            errorMessage={positionErrorMessage}
            buttonClassName="font-[Alexandria] text-sm lg:text-[16px] font-light dark:bg-[#0F1116] bg-[#FFFFFF] "
            dropdownClassName="font-[Alexandria] text-sm lg:text-[16px] font-light dark:bg-[#0F1116] bg-[#FFFFFF]"
          />

          {/* Cover Letter */}
          <Input
            id="coverLetter"
            label="Cover Letter"
            isRequired
            placeholder="Explain how you can contribute to ICIEOS's culture..."
            value={coverLetter}
            onChange={handleCoverChangeLive}
            onBlur={handleCoverBlur}
            errorMessage={coverErrorMessage}
            type="textarea"
            rows={6}
            maxLength={600}
            className="font-[Alexandria] text-sm lg:text-[16px] font-light dark:text-[#7F7F7F] text-[#5A5A6E] dark:bg-[#0F1116] bg-[#FFFFFF]"
          />

          {!coverErrorMessage && coverLetter && isCoverCharCountValid && (
            <p className="text-sm font-inter font-normal text-[#5A5A6E] dark:text-[#7F7F7F] -mt-5 mb-0">
              Cover letter must be 300-600 characters
            </p>
          )}

          <div className="flex justify-end">
            <p className="text-sm font-inter font-normal text-[#5A5A6E] dark:text-[#7F7F7F] -mt-7">
              {coverCharCount} / {maxCharCount}
            </p>
          </div>

          {/* Upload CV */}
          <div>
            <FormLabel htmlFor="cvUpload" label="Upload CV" isRequired />
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              className={twMerge(
                "relative rounded-lg p-4 text-center transition-colors",
                // error state overrides border color
                fileHasError
                  ? "border border-[#FF0000] dark:border-[#FF0000]"
                  : isDragging
                    ? "border border-[#7F7F7F] dark:bg-cyan-500/10 bg-[#f7f2ff]"
                    : "border border-[#12121257] dark:border-white/20 dark:bg-[#0F1116] bg-[#FFFFFF]",
              )}
            >
              <input
                type="file"
                id="cvUpload"
                onChange={handleFileChange}
                onBlur={handleFileBlur}
                accept=".pdf,.doc,.docx,.jpeg,.png"
                className="hidden"
              />
              <Upload className="w-12 h-12 text-gray-500 mx-auto mb-4" />

              {file ? (
                <div className="text-cyan-400 mb-2">
                  <p className="font-medium">{file.name}</p>
                  <p className="text-sm text-gray-500">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              ) : (
                <p className="font-alexandria font-normal text-[14px] sm:text-[16px] lg:text-[18px] 3xl:text-[20px] dark:text-[#7F7F7F] text-[#5A5A6E] mb-2">
                  Choose a file or drag & drop it here
                </p>
              )}

              <p
                aria-live="polite"
                className={`text-[#FF0000] text-[10px] sm:text-[12px] 3xl:text-[13px] 4xl:text-[14px] font-inter font-normal leading-3 block mt-2 min-h-5`}
              >
                {fileError || "\u00A0"}
              </p>

              <p className="font-alexandria font-light dark:text-[#7F7F7F] text-[#5A5A6E] text-xs sm:text-sm lg:text-[18px] 3xl:text-[20px] mb-4">
                JPEG, PNG, PDF, DOCX - up to 50MB
              </p>

              <label
                htmlFor="cvUpload"
                onClick={handleFileDialogStart}
                className="font-alexandria font-normal text-xs sm:text-sm lg:text-[16px] 3xl:text-[18px] dark:text-[#7F7F7F] text-[#5A5A6E] inline-block dark:bg-[#0F1116] bg-[#FFFFFF] border border-[#5e5e5e] dark:border-[#7f7f7f] hover:bg-[#F1F1F5] dark:hover:bg-[#26263A] rounded-[10px] px-6 py-2 cursor-pointer transition-colors"
              >
                BROWSE FILE
              </label>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-center pt-4">
            <Button
              type="submit"
              disabled={
                isSubmitting ||
                !isFormValid ||
                isFetchingPositions ||
                isSubmittingApp
              }
              className={twMerge(
                isDark ? "" : "bg-[#DF27DF]",
                "w-full md:w-auto font-albertSans font-semibold px-10 py-3",
              )}
            >
              {isSubmitting || isSubmittingApp ? "Submitting..." : "Submit"}
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
}
